/**
 * Vue Error Handler Plugin
 *
 * This plugin adds a global error handler to catch and normalize component errors
 * before they reach Sentry and other error handlers.
 *
 * Key responsibilities:
 * 1. **Error Normalization**: Converts ErrorEvents, strings, and malformed objects
 *    into proper Error objects with messages. This prevents h3's createError from
 *    generating "No error message" errors in Sentry.
 *
 * 2. **Zombie VNode Suppression**: Prevents "Cannot read properties of null (reading 'emitsOptions')"
 *    errors from unmounted components from propagating (expected during cleanup).
 *
 * 3. **Development Logging**: Logs errors to console in development for debugging,
 *    including both the normalized and original error for troubleshooting.
 *
 * 4. **Sentry Integration**: Ensures Sentry always receives proper Error objects
 *    with meaningful messages and stack traces.
 *
 * Context: Fixes Sentry "No error message" errors caused by ErrorEvents or malformed
 * error objects being passed to Nuxt's error handler, which eventually calls h3's
 * createError() expecting error.message or error.statusMessage.
 */

export default defineNuxtPlugin((nuxtApp) => {
  // Store the existing error handler (likely set by Sentry)
  const existingErrorHandler = nuxtApp.vueApp.config.errorHandler

  // Wrap the existing error handler instead of replacing it
  nuxtApp.vueApp.config.errorHandler = (err, instance, info) => {
    // Normalize the error to ensure it's always a proper Error object
    // This prevents h3's createError from creating errors with empty messages
    let normalizedError: Error

    // Check if this is an ErrorEvent (browser event with .error property)
    if (err && typeof err === 'object' && 'error' in err && err.error) {
      // Extract the real error from ErrorEvent
      normalizedError =
        err.error instanceof Error
          ? err.error
          : new Error(
              typeof err.error === 'object' && err.error !== null
                ? JSON.stringify(
                    err.error,
                    Object.getOwnPropertyNames(err.error),
                  )
                : String(err.error || err.message || 'Unknown error'),
            )
    } else if (err instanceof Error) {
      normalizedError = err
    } else if (typeof err === 'string') {
      normalizedError = new Error(err)
    } else {
      // Create a proper Error object with context for malformed errors
      const errorInfo =
        typeof err === 'object' && err !== null
          ? JSON.stringify(err, Object.getOwnPropertyNames(err))
          : String(err)
      normalizedError = new Error(
        `Vue Error (${info}): ${errorInfo || 'Unknown error'}`,
      )
      // Preserve original error as cause if possible
      if (typeof err === 'object' && err !== null) {
        ;(normalizedError as any).cause = err
      }
    }

    // Extract error message safely from normalized error
    const errorMessage = normalizedError.message
    const errorName = normalizedError.name

    // Check if this is a known "zombie VNode" error pattern
    const isZombieVNodeError =
      errorMessage.includes(
        "Cannot read properties of null (reading 'emitsOptions')",
      ) ||
      (errorMessage.includes('Cannot read properties of null') &&
        info.includes('component'))

    // If it's a zombie VNode error and component is unmounted, suppress it
    if (isZombieVNodeError) {
      // Check if the component instance exists and is still mounted
      const isComponentUnmounted =
        !instance ||
        (instance as any)?.isUnmounted ||
        (instance as any)?.$?.isUnmounted ||
        (instance as any)?.$.isUnmounting

      if (isComponentUnmounted) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[Vue Error Handler] Suppressed error from unmounted component:',
            {
              error: errorMessage,
              info,
              componentName:
                instance?.$options?.name ||
                instance?.$options?.__name ||
                'Unknown',
            },
          )
        }
        // Suppress this error - it's expected during cleanup
        return
      }
    }

    // For all other errors, log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('[Vue Error Handler]', {
        error: normalizedError,
        originalError: err !== normalizedError ? err : undefined,
        errorMessage,
        errorName,
        info,
        componentName:
          instance?.$options?.name || instance?.$options?.__name || 'Unknown',
        instance,
      })
    }

    // Pass the normalized error to the original error handler (Sentry's vueIntegration)
    // Now it will always receive a proper Error object with a message
    if (existingErrorHandler) {
      existingErrorHandler(normalizedError, instance, info)
    }
  }

  // Also set up a warning handler for development
  if (process.env.NODE_ENV === 'development') {
    nuxtApp.vueApp.config.warnHandler = (msg, instance, trace) => {
      // Only log warnings that might be related to our issue
      if (
        msg.includes('component') ||
        msg.includes('unmount') ||
        msg.includes('update') ||
        msg.includes('emitsOptions')
      ) {
        console.warn('[Vue Warning Handler]', {
          message: msg,
          componentName:
            instance?.$options?.name || instance?.$options?.__name || 'Unknown',
          trace,
        })
      }
    }
  }
})
